import { useState } from "react";
import {
  SERVICO_ICONES,
  SERVICO_ICONE_LABEL,
  WEEKDAYS,
  WEEKDAY_LABEL,
  formatCnpj,
  type Farmaceutico,
  type Farmacia,
  type Foto,
  type Horario,
  type ServicoIcone,
} from "@farmacias/schema";
import { Field, Section, Select, StringList, Text, TextArea } from "./Fields";
import { PhotoField } from "./PhotoField";

const ICONE_OPTIONS = SERVICO_ICONES.map((v) => ({ value: v, label: SERVICO_ICONE_LABEL[v] }));

type Issues = Map<string, string>;

export function FarmaciaForm({
  value,
  onChange,
  issues,
  onUploadLogo,
  onUploadFoto,
}: {
  value: Farmacia;
  onChange: (f: Farmacia) => void;
  issues: Issues;
  onUploadLogo: (file: File) => Promise<void>;
  onUploadFoto: (tipo: "hero" | "fachada", file: File) => Promise<void>;
}) {
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoErr, setLogoErr] = useState<string | null>(null);

  const set = (patch: Partial<Farmacia>) => onChange({ ...value, ...patch });
  const setEndereco = (patch: Partial<Farmacia["endereco"]>) =>
    onChange({ ...value, endereco: { ...value.endereco, ...patch } });
  const setCnpj = (i: number, v: string) =>
    set({ cnpjs: value.cnpjs.map((c, idx) => (idx === i ? v : c)) });
  const addCnpj = () => set({ cnpjs: [...value.cnpjs, ""] });
  const removeCnpj = (i: number) => set({ cnpjs: value.cnpjs.filter((_, idx) => idx !== i) });

  const setFarma = (i: number, patch: Partial<Farmaceutico>) =>
    set({ farmaceuticos: value.farmaceuticos.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });
  const addFarma = () => set({ farmaceuticos: [...value.farmaceuticos, { nome: "", crf: "" }] });
  const removeFarma = (i: number) =>
    set({ farmaceuticos: value.farmaceuticos.filter((_, idx) => idx !== i) });
  const e = (k: string) => issues.get(k);

  const setHorario = (i: number, patch: Partial<Horario>) => {
    const horarios = value.horarios.map((h, idx) => (idx === i ? { ...h, ...patch } : h));
    set({ horarios });
  };
  const addHorario = () =>
    set({ horarios: [...value.horarios, { dia: "seg", abre: "08:00", fecha: "18:00" }] });
  const removeHorario = (i: number) => set({ horarios: value.horarios.filter((_, idx) => idx !== i) });

  const setServico = (i: number, patch: Partial<Farmacia["servicos"][number]>) =>
    set({ servicos: value.servicos.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const addServico = () =>
    set({ servicos: [...value.servicos, { titulo: "", descricao: "", icone: "mais" }] });
  const removeServico = (i: number) => set({ servicos: value.servicos.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-5">
      <Section title="Identificação">
        <Text
          label="Razão social"
          required
          value={value.razaoSocial}
          onChange={(v) => set({ razaoSocial: v })}
          error={e("razaoSocial")}
        />
        <Text
          label="Nome fantasia"
          required
          value={value.nomeFantasia}
          onChange={(v) => set({ nomeFantasia: v })}
          error={e("nomeFantasia")}
        />
        <Field
          label="CNPJ"
          required
          error={e("cnpjs")}
          hint="Matriz e filiais. Só números ou com máscara — validamos o dígito verificador."
        >
          <div className="space-y-2">
            {value.cnpjs.map((cnpj, i) => (
              <div key={i}>
                <div className="flex items-center gap-2">
                  <input
                    className="input"
                    value={cnpj}
                    placeholder="00.000.000/0001-00"
                    onChange={(ev) => setCnpj(i, ev.target.value)}
                    onBlur={() => setCnpj(i, formatCnpj(cnpj))}
                  />
                  <button
                    type="button"
                    className="btn-ghost !px-2 !py-1 text-xs"
                    aria-label={`Remover CNPJ ${i + 1}`}
                    onClick={() => removeCnpj(i)}
                  >
                    ✕
                  </button>
                </div>
                {e(`cnpjs.${i}`) && <p className="mt-1 text-xs text-red-600">{e(`cnpjs.${i}`)}</p>}
              </div>
            ))}
            <button type="button" className="btn-ghost text-xs" onClick={addCnpj}>
              + Adicionar CNPJ
            </button>
          </div>
        </Field>
      </Section>

      <Section title="Apresentação">
        <Text
          label="Selo acima do título"
          value={value.badge}
          onChange={(v) => set({ badge: v })}
          hint='Opcional. Ex.: "Há mais de 30 anos em Nuporanga."'
          placeholder="Há mais de 30 anos em Nuporanga"
        />
        <TextArea
          label="Título do hero (H1)"
          value={value.headline}
          onChange={(v) => set({ headline: v })}
          hint="Opcional. Trechos entre *asteriscos* saem na cor da marca. Em branco, geramos a partir do bairro/cidade."
        />
        <TextArea
          label="Frase curta (hero / SEO)"
          value={value.descricaoCurta}
          onChange={(v) => set({ descricaoCurta: v })}
          hint='Opcional. Ex.: "Sua farmácia na Vila Mariana, com entrega no bairro."'
        />
        <StringList
          label="Diferenciais (bullets do hero)"
          values={value.diferenciais}
          onChange={(diferenciais) => set({ diferenciais })}
          max={6}
          placeholder="Entrega gratuita no bairro"
          hint="Opcional, até 6. Aparecem com um check ao lado do título."
        />
        <TextArea
          label='Texto da seção "Sobre"'
          rows={6}
          value={value.sobre}
          onChange={(v) => set({ sobre: v })}
          hint="Opcional. Separe parágrafos com uma linha em branco. Em branco, usamos a frase curta."
        />
        <Text
          label="Cor da marca (hex)"
          value={value.theme?.cor ?? ""}
          onChange={(v) => set({ theme: v ? { cor: v } : null })}
          error={e("theme.cor")}
          hint="Opcional. Ex.: #0F7B4A"
          placeholder="#0F7B4A"
        />
      </Section>

      <Section title="Endereço">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Text label="Logradouro" required value={value.endereco.logradouro} onChange={(v) => setEndereco({ logradouro: v })} error={e("endereco.logradouro")} />
          </div>
          <Text label="Número" required value={value.endereco.numero} onChange={(v) => setEndereco({ numero: v })} error={e("endereco.numero")} />
        </div>
        <Text label="Complemento" value={value.endereco.complemento ?? ""} onChange={(v) => setEndereco({ complemento: v })} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Text label="Bairro" required value={value.endereco.bairro} onChange={(v) => setEndereco({ bairro: v })} error={e("endereco.bairro")} />
          <Text label="Cidade" required value={value.endereco.cidade} onChange={(v) => setEndereco({ cidade: v })} error={e("endereco.cidade")} />
          <Text label="UF" required value={value.endereco.uf} onChange={(v) => setEndereco({ uf: v.toUpperCase().slice(0, 2) })} error={e("endereco.uf")} />
        </div>
        <Text label="CEP" required value={value.endereco.cep} onChange={(v) => setEndereco({ cep: v })} error={e("endereco.cep")} placeholder="00000-000" />
      </Section>

      <Section title="Contato">
        <Text label="Telefone comercial" required value={value.telefoneComercial} onChange={(v) => set({ telefoneComercial: v })} error={e("telefoneComercial")} placeholder="(11) 3333-4444" />
        <Text label="WhatsApp" required value={value.whatsapp} onChange={(v) => set({ whatsapp: v })} error={e("whatsapp")} placeholder="(11) 99999-8888" />
        <Text label="E-mail" type="email" value={value.email} onChange={(v) => set({ email: v })} error={e("email")} hint="Opcional." />
        <Text
          label="Instagram"
          value={value.instagram}
          onChange={(v) => set({ instagram: v })}
          hint="Opcional. @usuario ou a URL completa."
          placeholder="@farmaciabemestar"
        />
      </Section>

      <Section title="Farmacêutico(a) responsável">
        {issues.get("farmaceuticos") && (
          <p className="text-xs text-red-600">{issues.get("farmaceuticos")}</p>
        )}
        <div className="space-y-3">
          {value.farmaceuticos.map((rt, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Text
                label="Nome"
                required
                value={rt.nome}
                onChange={(v) => setFarma(i, { nome: v })}
                error={e(`farmaceuticos.${i}.nome`)}
              />
              <Text
                label="CRF"
                required
                value={rt.crf}
                onChange={(v) => setFarma(i, { crf: v })}
                error={e(`farmaceuticos.${i}.crf`)}
                placeholder="CRF-SP 12345"
              />
              <button
                type="button"
                className="btn-ghost !px-2 !py-1 text-xs"
                onClick={() => removeFarma(i)}
              >
                Remover responsável
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-ghost text-xs" onClick={addFarma}>
          + Adicionar responsável
        </button>
      </Section>

      <Section title="Horário de funcionamento">
        {issues.get("horarios") && <p className="text-xs text-red-600">{issues.get("horarios")}</p>}
        <div className="space-y-2">
          {value.horarios.map((h, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <select className="input max-w-[9rem]" value={h.dia} onChange={(ev) => setHorario(i, { dia: ev.target.value as Horario["dia"] })}>
                {WEEKDAYS.map((d) => (
                  <option key={d} value={d}>
                    {WEEKDAY_LABEL[d]}
                  </option>
                ))}
              </select>
              <input className="input max-w-[7rem]" type="time" value={h.abre} onChange={(ev) => setHorario(i, { abre: ev.target.value })} />
              <span className="text-slate-400">às</span>
              <input className="input max-w-[7rem]" type="time" value={h.fecha} onChange={(ev) => setHorario(i, { fecha: ev.target.value })} />
              <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => removeHorario(i)}>
                Remover
              </button>
              {issues.get(`horarios.${i}`) && (
                <span className="w-full text-xs text-red-600">{issues.get(`horarios.${i}`)}</span>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="btn-ghost text-xs" onClick={addHorario}>
          + Adicionar horário
        </button>
      </Section>

      <Section title="Logo">
        <div className="flex items-center gap-4">
          {value.logo ? (
            <img src={value.logo.url} alt="Logo atual" className="h-16 w-16 rounded border border-slate-200 object-contain p-1" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              sem logo
            </div>
          )}
          <div>
            <input
              type="file"
              accept="image/png,image/svg+xml,image/jpeg"
              disabled={logoBusy}
              onChange={async (ev) => {
                const file = ev.target.files?.[0];
                if (!file) return;
                setLogoBusy(true);
                setLogoErr(null);
                try {
                  await onUploadLogo(file);
                } catch (err) {
                  setLogoErr(err instanceof Error ? err.message : "Falha no upload");
                } finally {
                  setLogoBusy(false);
                  ev.target.value = "";
                }
              }}
            />
            <p className="mt-1 text-xs text-slate-400">PNG ou SVG (JPEG aceito). Máx. 5 MB.</p>
            {logoBusy && <p className="text-xs text-slate-500">Enviando…</p>}
            {logoErr && <p className="text-xs text-red-600">{logoErr}</p>}
            {issues.get("logo") && <p className="text-xs text-red-600">{issues.get("logo")}</p>}
          </div>
        </div>
      </Section>

      <Section title="Fotos da landing (opcional)">
        <PhotoField
          label="Foto do topo (hero)"
          hint="JPEG, PNG ou WEBP até 8 MB. Ideal na horizontal (5:4). Sem foto, o topo fica em coluna única."
          altHint='Descreva a cena. Ex.: "Farmacêutica atendendo uma cliente no balcão."'
          value={value.fotoHero}
          onUpload={(file) => onUploadFoto("hero", file)}
          onChange={(fotoHero: Foto | null) => set({ fotoHero })}
        />
        <PhotoField
          label="Foto da fachada"
          hint="JPEG, PNG ou WEBP até 8 MB. Ideal na vertical (4:5). Sem foto, mostramos o mapa no lugar."
          altHint='Descreva a fachada. Ex.: "Fachada da Farmácia Bem Estar na Rua X."'
          value={value.fotoFachada}
          onUpload={(file) => onUploadFoto("fachada", file)}
          onChange={(fotoFachada: Foto | null) => set({ fotoFachada })}
        />
      </Section>

      <Section title="Serviços (opcional)">
        <div className="space-y-3">
          {value.servicos.map((s, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Text label="Título" value={s.titulo} onChange={(v) => setServico(i, { titulo: v })} />
              <Text label="Descrição" value={s.descricao ?? ""} onChange={(v) => setServico(i, { descricao: v })} />
              <Select
                label="Ícone"
                value={s.icone}
                options={ICONE_OPTIONS}
                onChange={(icone: ServicoIcone) => setServico(i, { icone })}
              />
              <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => removeServico(i)}>
                Remover serviço
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-ghost text-xs" onClick={addServico}>
          + Adicionar serviço
        </button>
      </Section>

      <Section title="Licença (opcional)">
        <Field label="Licença sanitária / AFE">
          <input className="input" value={value.licencaSanitaria} onChange={(ev) => set({ licencaSanitaria: ev.target.value })} />
        </Field>
      </Section>
    </div>
  );
}
